/**
 * Dashboard persistence and lifecycle.
 *
 * A dashboard IS an AppModule with `kind: 'dashboard'`, so ownership, sharing,
 * assignment, and the landing page work through the existing moduleService
 * access boundary with no parallel permission system. Only the dashboard's own
 * configuration lives in `module.dashboard`.
 *
 * All writes go through `moduleService.updateModule(user, …)`, which
 * re-validates edit rights against persisted state — a shared dashboard cannot
 * be saved by its recipient even if the UI were bypassed.
 */

import type { AppModule, LayerGeometryType, User } from '../../types';
import { moduleService } from '../../services/moduleService';
import { createId } from '../../services/storage';
import {
  DASHBOARD_SCHEMA_VERSION,
  type DashboardConfig,
  type DashboardDataSource,
  type DashboardServiceType,
  type DashboardWidget,
  type WidgetType,
} from '../types/dashboardTypes';
import { WIDGET_LIBRARY } from '../types/dashboardTypes';
import { nextFreeRow, validateWidget } from '../utils/dashboardUtils';

export function createDefaultDashboardConfig(): DashboardConfig {
  return {
    schemaVersion: DASHBOARD_SCHEMA_VERSION,
    description: '',
    dataSources: [],
    widgets: [],
    selectors: [],
    theme: { accentColor: '#2563EB', mode: 'light' },
    refreshInterval: 0,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Brings a stored config up to the current schema.
 *
 * Called on EVERY read, so a dashboard saved by an older build keeps opening.
 * When the schema changes, add a step here rather than changing the readers.
 */
export function migrateDashboard(raw: unknown): DashboardConfig {
  const defaults = createDefaultDashboardConfig();
  if (!raw || typeof raw !== 'object') return defaults;

  const stored = raw as Partial<DashboardConfig>;

  // v0 (no schemaVersion) → v1: fill in fields added after the first release.
  const config: DashboardConfig = {
    ...defaults,
    ...stored,
    schemaVersion: DASHBOARD_SCHEMA_VERSION,
    dataSources: Array.isArray(stored.dataSources) ? stored.dataSources : [],
    widgets: Array.isArray(stored.widgets) ? stored.widgets : [],
    // Absent on every dashboard saved before selectors existed.
    selectors: Array.isArray(stored.selectors) ? stored.selectors : [],
    theme: { ...defaults.theme, ...(stored.theme ?? {}) },
  };

  // Defensive: widgets written by a future/partial build may miss members the
  // renderers assume are present.
  config.widgets = config.widgets
    .filter((widget) => widget && typeof widget.id === 'string')
    .map((widget) => ({
      ...widget,
      filters: Array.isArray(widget.filters) ? widget.filters : [],
      config: widget.config ?? {},
      layout: widget.layout ?? { x: 0, y: 0, w: 3, h: 2 },
    }));

  return config;
}

/** Reads a module's dashboard config, migrated and safe to render. */
export function getDashboardConfig(module: AppModule): DashboardConfig {
  return migrateDashboard(module.dashboard);
}

export function isDashboard(module: AppModule): boolean {
  return module.kind === 'dashboard';
}

/** A new, empty dashboard owned by its creator. */
export function createDashboardModule(name: string, owner: User): AppModule {
  return {
    id: createId('dash'),
    name,
    title: name,
    kind: 'dashboard',
    built: false,
    // Map-app fields stay empty; a dashboard's layers live in `dashboard`.
    layers: [],
    tools: [],
    geoServerUrl: '',
    geoServerLayers: [],
    homeCenter: [28.7041, 77.1025],
    homeZoom: 12,
    ownerId: owner.id,
    visibility: owner.role === 'admin' ? 'admin-workspace' : 'user-owned',
    createdAt: new Date().toISOString(),
    dashboard: createDefaultDashboardConfig(),
  };
}

/** A new widget with sensible defaults, placed below everything else. */
export function createWidget(type: WidgetType, existing: DashboardWidget[]): DashboardWidget {
  const definition = WIDGET_LIBRARY.find((entry) => entry.type === type);
  const size = definition?.defaultLayout ?? { w: 3, h: 2 };

  const base: DashboardWidget = {
    id: createId('wgt'),
    type,
    title: definition?.label ?? 'Widget',
    filters: [],
    config: {},
    layout: { x: 0, y: nextFreeRow(existing), w: size.w, h: size.h },
  };

  switch (type) {
    case 'kpi':
      base.config = { statistic: 'count', decimals: 0 };
      break;
    case 'gauge':
      base.config = { statistic: 'count', minValue: 0, maxValue: 100, decimals: 0 };
      break;
    case 'pie':
    case 'donut':
    case 'bar':
    case 'column':
    case 'line':
      base.config = {
        statistic: 'count',
        maxCategories: 8,
        sortBy: 'value-desc',
        showLegend: type === 'pie' || type === 'donut',
        showValueLabels: true,
      };
      break;
    case 'table':
      base.config = { columns: [], pageSize: 10 };
      break;
    case 'list':
      base.config = { maxItems: 50 };
      break;
    case 'map':
      base.config = { mapDataSourceIds: [], basemap: 'street' };
      break;
    case 'text':
      base.config = { heading: 'Heading', body: 'Add your text here.', align: 'left' };
      break;
    default:
      break;
  }

  return base;
}

export interface DashboardValidation {
  ok: boolean;
  /** Blocking problems — publishing is refused while any exist. */
  errors: string[];
  /** Non-blocking advice. */
  warnings: string[];
}

/**
 * Checks a dashboard before Save & Launch.
 *
 * The bar is "an end user would see a broken widget", because the viewer has no
 * controls to fix anything.
 */
export function validateDashboard(config: DashboardConfig): DashboardValidation {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (config.widgets.length === 0) {
    errors.push('Add at least one widget before launching.');
  }

  const sourceIds = new Set(config.dataSources.map((source) => source.id));

  for (const widget of config.widgets) {
    const label = widget.title || widget.type;

    if (widget.dataSourceId && !sourceIds.has(widget.dataSourceId)) {
      errors.push(`“${label}” points at a data source that has been removed.`);
      continue;
    }

    for (const problem of validateWidget(widget)) {
      errors.push(`“${label}”: ${problem}`);
    }
  }

  for (const selector of config.selectors ?? []) {
    const label = selector.label || `${selector.type} selector`;
    if (selector.dataSourceId && !sourceIds.has(selector.dataSourceId)) {
      errors.push(`“${label}” points at a data source that has been removed.`);
    } else if (!selector.field) {
      // Harmless — an unconfigured selector just contributes no filter — but
      // worth a nudge, the same as a widget the Setup badge already flags.
      warnings.push(`“${label}” has no field chosen yet, so it does not filter anything.`);
    }
  }

  const unused = config.dataSources.filter(
    (source) =>
      !config.widgets.some(
        (widget) =>
          widget.dataSourceId === source.id ||
          widget.config.mapDataSourceIds?.includes(source.id),
      ) && !(config.selectors ?? []).some((selector) => selector.dataSourceId === source.id),
  );
  if (unused.length > 0) {
    warnings.push(
      `${unused.length} data source${unused.length === 1 ? ' is' : 's are'} not used by any widget.`,
    );
  }

  return { ok: errors.length === 0, errors, warnings };
}

export interface SaveOptions {
  /** Marks the dashboard Live so it opens in the viewer from the landing page. */
  publish?: boolean;
}

/**
 * Persists a dashboard through the guarded module service.
 *
 * Async because the write is now a request that can be refused — by
 * permission, by a version conflict, or by the network. Returning before it
 * lands would tell the builder "Dashboard saved" for a save that never
 * happened.
 *
 * Throws ModulePermissionError when the user may not edit this module.
 */
export async function saveDashboard(
  user: User | null,
  module: AppModule,
  config: DashboardConfig,
  options: SaveOptions = {},
): Promise<AppModule> {
  const nextModule: AppModule = {
    ...module,
    kind: 'dashboard',
    built: options.publish ? true : module.built,
    dashboard: {
      ...config,
      schemaVersion: DASHBOARD_SCHEMA_VERSION,
      updatedAt: new Date().toISOString(),
    },
  };

  await moduleService.updateModule(user, nextModule);
  return nextModule;
}

/** Removes a data source and detaches every widget that referenced it. */
export function removeDataSource(
  config: DashboardConfig,
  sourceId: string,
): DashboardConfig {
  return {
    ...config,
    dataSources: config.dataSources.filter((source) => source.id !== sourceId),
    widgets: config.widgets.map((widget) => ({
      ...widget,
      dataSourceId: widget.dataSourceId === sourceId ? undefined : widget.dataSourceId,
      config: {
        ...widget.config,
        mapDataSourceIds: widget.config.mapDataSourceIds?.filter((id) => id !== sourceId),
      },
    })),
    // A selector's data source is required, unlike a widget's — nothing left
    // for it to point at once removed, so it goes with the source rather than
    // sitting inert in the Filters list.
    selectors: (config.selectors ?? []).filter((selector) => selector.dataSourceId !== sourceId),
  };
}

/** Stable label for a data source in pickers and widget footers. */
export function describeDataSource(source: DashboardDataSource): string {
  const service =
    source.serviceType === 'local'
      ? source.originFile
        ? `File · ${source.originFile}`
        : 'Imported file'
      : source.serviceType === 'geoserver'
        ? 'GeoServer'
        : 'ArcGIS REST';
  const geometry =
    source.geometryType && source.geometryType !== 'unknown'
      ? ` | ${source.geometryType[0].toUpperCase()}${source.geometryType.slice(1)}`
      : '';
  return `${service}${geometry}`;
}

/* ─────────────────────────── Sources from a map app ──────────────────────── */

/**
 * Turns a map application's layers into dashboard data sources.
 *
 * The point of this path: a map app is where the URLs, the layer names and the
 * geometry types have ALREADY been worked out. Retyping a GeoServer URL and
 * re-picking twelve layers to chart what the map already draws is work the
 * app can do instead.
 *
 * What it deliberately skips, and why:
 *  - Scratch layers. One person's buffer is not a data source for everyone
 *    looking at a dashboard.
 *  - Layers whose service URL is missing on the module — usually a layer left
 *    behind after its service was changed.
 */
export interface AppSourceCandidate {
  layerId: string;
  name: string;
  serviceType: DashboardServiceType;
  serviceUrl: string;
  layerName: string;
  arcgisLayerId?: number;
  geometryType: LayerGeometryType;
  /** The features themselves, when the layer was imported from a file. */
  localData?: GeoJSON.FeatureCollection;
  /** Set when the layer cannot become a source; explains why. */
  unusable?: string;
}

export function describeAppLayers(module: AppModule): AppSourceCandidate[] {
  return (module.layers ?? [])
    .filter((layer) => !layer.temporary)
    .map((layer) => {
      const base = {
        layerId: layer.id,
        name: layer.displayName?.trim() || layer.name,
        layerName: layer.name,
        geometryType: layer.geometryType ?? ('unknown' as LayerGeometryType),
      };

      /* A layer imported into the map app. Its features are COPIED into the
         dashboard rather than referenced, because the two documents are saved
         separately: a dashboard that pointed at the map's copy would break the
         day somebody edited or deleted that layer, and a viewer with no access
         to the map app could never load it at all. */
      if (layer.localData) {
        return {
          ...base,
          serviceType: 'local' as DashboardServiceType,
          serviceUrl: '',
          localData: layer.localData,
          unusable:
            (layer.localData.features ?? []).length === 0
              ? 'This layer has no features.'
              : undefined,
        };
      }

      if (layer.arcgisLayerId !== undefined) {
        const serviceUrl = (module.arcgisUrl ?? '').trim();
        return {
          ...base,
          serviceType: 'arcgis' as DashboardServiceType,
          serviceUrl,
          arcgisLayerId: layer.arcgisLayerId,
          unusable: serviceUrl ? undefined : 'This map has no ArcGIS service URL.',
        };
      }

      const serviceUrl = (module.geoServerUrl ?? '').trim();
      return {
        ...base,
        serviceType: 'geoserver' as DashboardServiceType,
        serviceUrl,
        unusable: serviceUrl ? undefined : 'This map has no GeoServer URL.',
      };
    });
}

/** A candidate as a data source, ready to add. */
export function candidateToSource(candidate: AppSourceCandidate): DashboardDataSource {
  return {
    id: createId('ds'),
    name: candidate.name,
    serviceType: candidate.serviceType,
    serviceUrl: candidate.serviceUrl,
    layerName: candidate.layerName,
    arcgisLayerId: candidate.arcgisLayerId,
    geometryType: candidate.geometryType,
    localData: candidate.localData,
    originFile: candidate.localData ? candidate.name : undefined,
    addedAt: new Date().toISOString(),
  };
}

/** True when this source is already on the dashboard. */
export function sourceExists(config: DashboardConfig, candidate: AppSourceCandidate): boolean {
  return config.dataSources.some(
    (source) =>
      // Included since local sources exist: they all have an empty serviceUrl,
      // so without the kind a shapefile called "roads" would look identical to
      // a GeoServer layer called "roads" whose URL is missing.
      source.serviceType === candidate.serviceType &&
      source.serviceUrl === candidate.serviceUrl &&
      source.layerName === candidate.layerName &&
      source.arcgisLayerId === candidate.arcgisLayerId,
  );
}
